import { assertBalanced } from "./ledger.js";
import { StateMachine } from "./state-machine.js";
/**
 * SPEC.md §9.1 lists more (Tap to Pay references, pledges, matching gifts, adjustments).
 * Deliberately not here yet: a `pledge` item posting to `offline_received` would be a pledge
 * counted as received funds, and Tap to Pay must reconcile against Stripe — each needs its own
 * treatment, not a bigger enum.
 */
export const OFFLINE_ITEM_TYPES = ["cash", "check", "external_card"];
export const OFFLINE_BATCH_STATES = [
    "draft",
    "submitted",
    "verified",
    "posted",
    "reconciled",
    "closed",
    "rejected",
    "voided",
];
/**
 * Draft → Submitted → Verified → Posted → Reconciled → Closed, with Rejected/Voided branches
 * (SPEC.md §9.2). Posting is the last transition implemented today; reconciled/closed exist in
 * the machine so the later reconciliation work extends states rather than migrating them.
 */
export const offlineBatchMachine = new StateMachine({
    name: "offline_batch",
    initial: "draft",
    transitions: {
        draft: ["submitted", "voided"],
        submitted: ["verified", "rejected", "draft"],
        verified: ["posted"],
        posted: ["reconciled"],
        reconciled: ["closed"],
        closed: [],
        rejected: [],
        voided: [],
    },
    progression: ["draft", "submitted", "verified", "posted", "reconciled", "closed"],
    terminal: ["closed", "rejected", "voided"],
});
/**
 * Cash/check totals at or above this require a second person: whoever submitted the batch
 * cannot also be the one who verifies it (SPEC.md §9.2). $500 as the default until an
 * organization-level setting exists; deployments can lower it, never really need to raise it.
 */
export const DEFAULT_DUAL_VERIFICATION_THRESHOLD_MINOR = 50000n;
export function requiresDualVerification(cashAndCheckTotalMinor, thresholdMinor = DEFAULT_DUAL_VERIFICATION_THRESHOLD_MINOR) {
    return cashAndCheckTotalMinor >= thresholdMinor;
}
/**
 * The ledger footprint of one posted entry.
 *
 * Both legs stay inside the "recorded elsewhere" world: the organization holds the cash
 * (`organization_cash_on_hand`) and the gift is recognised (`offline_received`). Neither touches
 * `contributions_received`, so envelope cash can never reach a payout balance — paying an organization money
 * that is already in its own cash box is the specific error this shape exists to make unreachable,
 * and there is a test named for it.
 *
 * The debit was `donations_receivable` until migration 0034, which is to say the books stated that
 * a payment processor was holding the contents of an envelope. Nothing downstream noticed, because
 * the goal projection reads the credit leg and the payout balance did not exist yet to read the
 * wrong one.
 *
 * One transaction per entry — not per batch — so a single mistaken envelope can be reversed without
 * touching its neighbours.
 */
export function offlinePostedTransaction(input) {
    const draft = {
        transactionId: input.transactionId,
        organizationId: input.organizationId,
        currency: input.amount.currency,
        memo: input.memo,
        entries: [
            {
                account: "organization_cash_on_hand",
                direction: "debit",
                amount: input.amount,
                memo: input.memo,
            },
            { account: "offline_received", direction: "credit", amount: input.amount, memo: input.memo },
        ],
    };
    assertBalanced(draft);
    return draft;
}
//# sourceMappingURL=offline-batch.js.map